import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useSound from "use-sound";
import InputField from "../InputField/InputField";
import { useDatabaseConnectionViewModel } from "../../viewmodels/DatabaseConnectionViewModel";
import { useDbConnection } from "../../hooks/useDbConnection";
import { useDatabase } from "../../contexts/DatabaseContext";
import { StorageHelper } from "../../utils/storageHelper";
import {
  ActionButtons,
  ConnectionButton,
  ConnectionIcon,
  ConnectionInfo,
  ConnectionPulse,
  ConnectionStatus,
  LogoContainer,
  MenuHeader,
  MenuTitle,
  NotificationBadge,
  NotificationLink,
  NotificationList,
  NotificationListItem,
  NotificationMessage,
  NotificationTime,
  ReadMoreButton,
  StyledAppBar,
  StyledIconButton,
  StyledMenu,
  StyledToolbar,
} from "./Topbar.styled";
import { useAuth } from "../../hooks/useAuth";
import CircularProgress from "@mui/material/CircularProgress";
import { useNotification } from "../../hooks/useNotification";
import { useUploadFile } from "../../hooks/useUploadFile";

const LOCAL_STORAGE_KEY = "selected_user_db_id";

const Topbar = ({ sidebarCollapsed, userDetails }) => {
  const navigate = useNavigate();
  const {
    useNotify,
    notificationsData,
    notificationsLoading,
    markAsRead,
    cacheOperations,
    refetchNotifications,
  } = useNotification();
  const { data: notify, loading: notiLoading } = useNotify;
  const { downloadFileUrl } = useUploadFile();

  const { user: authUser } = useAuth();
  const { getAllUserDatabases, getConnectionDetails } = useDbConnection();
  const { updateDatabaseState } = useDatabase();

  // Database dropdown state
  const [dbDropdownAnchor, setDbDropdownAnchor] = useState(null);
  const [userDatabases, setUserDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(false);
  const [dbDetailsLoading, setDbDetailsLoading] = useState(false);

  const [notification, setNotification] = React.useState([]);
  const [readNotifications, setReadNotifications] = React.useState(new Set());
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [play, { stop }] = useSound("/sounds/notification-20-270145.mp3", {
    volume: 0.5,
  });

  React.useEffect(() => {
    if (
      !notificationsLoading &&
      notificationsData?.GetNotificationsByUserId?.notifications
    ) {
      const notifications =
        notificationsData.GetNotificationsByUserId.notifications;
      setNotification(notifications);
      const readIds = notifications
        .filter((notif) => notif.is_read)
        .map((notif) => notif.notification_id);
      setReadNotifications(new Set(readIds));
      // console.log("notifications triggering setNotification", notifications);
    }
  }, [notificationsData, notificationsLoading]);

  React.useEffect(() => {
    // console.log("notiLoading", notiLoading);
    // console.log("LOGGED-IN USER ID", StorageHelper.get("userDetails")?.user_id.toString());
    // console.log("NOTIFICATION USER ID FROM NOTIFY HOOK", notify?.PushNotification?.user_id.toString());
    if (
      !notiLoading &&
      StorageHelper.get("userDetails")?.user_id.toString() ===
      notify?.PushNotification?.user_id.toString()
    ) {
      const newNotification = notify?.PushNotification;
      console.log("newNotification", newNotification);
      if (newNotification) {
        play();
        console.log("play notification", newNotification);
        const resourceId = parseInt(
          StorageHelper.get("userDetails")?.user_id.toString()
        );
        const currentNotifications =
          cacheOperations.readNotifications(resourceId);
        const exists = currentNotifications.some(
          (n) => n.notification_id === newNotification.notification_id
        );

        if (!exists) {
          const updatedNotifications = [
            newNotification,
            ...currentNotifications,
          ];
          cacheOperations.writeNotifications(updatedNotifications, resourceId);
          setNotification(updatedNotifications);
          if (newNotification.is_read) {
            setReadNotifications(
              (prev) => new Set([...prev, newNotification.notification_id])
            );
          }
        }
      }
    }
  }, [notify, notiLoading, cacheOperations, play]);

  // Database connection state
  const [openDbModal, setOpenDbModal] = useState(false);
  const {
    connectionState,
    connect,
    disconnect,
    updateConnectionDetails,
    onError,
  } = useDatabaseConnectionViewModel();

  // Fetch user databases
  const userId = authUser?.userDetails?.user_id;
  useEffect(() => {
    if (!userId) return;
    const fetchUserDatabases = async () => {
      try {
        setIsLoadingDatabases(true);
        const result = await getAllUserDatabases(userId);
        if (result.success && result.data) {
          setUserDatabases(result.data);
        }
      } catch (error) {
        console.error("Error fetching user databases:", error);
      } finally {
        setIsLoadingDatabases(false);
      }
    };
    fetchUserDatabases();
  }, [userId, getAllUserDatabases]);

  // On mount, check localStorage for previously selected DB
  useEffect(() => {
    if (userDatabases && userDatabases.length > 0) {
      const storedDbId = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedDbId) {
        const foundDb = userDatabases.find(
          (db) => db.user_db_id === storedDbId
        );
        if (foundDb) {
          setSelectedDatabase(foundDb);
        }
      }
    }
  }, [userDatabases]);

  useEffect(() => {
    const handleDbIdChange = () => {
      const storedDbId = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedDbId && userDatabases && userDatabases.length > 0) {
        const foundDb = userDatabases.find(
          (db) => db.user_db_id === storedDbId
        );
        if (foundDb) {
          setSelectedDatabase(foundDb);
        }
      }
    };
    window.addEventListener("selected_user_db_id_changed", handleDbIdChange);
    return () => {
      window.removeEventListener(
        "selected_user_db_id_changed",
        handleDbIdChange
      );
    };
  }, [userDatabases]);

  const handleDbDropdownClick = (event) => {
    setDbDropdownAnchor(event.currentTarget);
  };

  const handleDbDropdownClose = () => {
    setDbDropdownAnchor(null);
  };

  const handleDatabaseSelect = async (db) => {
    if (db === "add_new") {
      handleDbModalOpen();
      handleDbDropdownClose();
      return;
    }
    setSelectedDatabase(db);
    localStorage.setItem(LOCAL_STORAGE_KEY, db.user_db_id);
    window.dispatchEvent(new Event("selected_user_db_id_changed"));
    // Always fetch latest connection details and tablesData for the selected DB
    if (userId && db.user_db_id) {
      setDbDetailsLoading(true);
      try {
        const detailsResult = await getConnectionDetails(userId, db.user_db_id);
        if (detailsResult.success) {
          let tablesData = detailsResult.data.tablesData;
          if (typeof tablesData === "string") {
            try {
              tablesData = JSON.parse(tablesData);
            } catch (e) {
              console.error("Error parsing tablesData:", e);
            }
          }
          const datasourceId = detailsResult.data.user_db_id;
          console.log("datasourceId", datasourceId);

          // Save to localStorage
          localStorage.setItem(
            "snowflake_connection_details",
            JSON.stringify(detailsResult.data.connectionDetails || {})
          );
          localStorage.setItem(
            "snowflake_tables_data",
            JSON.stringify(tablesData)
          );
          if (datasourceId) {
            localStorage.setItem("snowflake_datasource_id", datasourceId);
          }
          // Update global database state so Sidebar and others refresh
          updateDatabaseState({
            isConnected: true,
            user_db_id: db.user_db_id,
            tablesData: tablesData,
            datasourceId: datasourceId,
          });
        } else {
          // Clear if not present for this DB
          localStorage.removeItem("snowflake_connection_details");
          localStorage.removeItem("snowflake_tables_data");
          localStorage.removeItem("snowflake_datasource_id");
          updateDatabaseState({
            isConnected: false,
            user_db_id: db.user_db_id,
            tablesData: null,
            datasourceId: null,
          });
        }
      } catch (error) {
        console.error(
          "Error fetching connection details for selected DB:",
          error
        );
        localStorage.removeItem("snowflake_connection_details");
        localStorage.removeItem("snowflake_tables_data");
        localStorage.removeItem("snowflake_datasource_id");
        updateDatabaseState({
          isConnected: false,
          user_db_id: db.user_db_id,
          tablesData: null,
          datasourceId: null,
        });
      } finally {
        setDbDetailsLoading(false);
      }
    }
    handleDbDropdownClose();
    if (userId) {
      try {
        setIsLoadingDatabases(true);
        const result = await getAllUserDatabases(userId);
        if (result.success && result.data) {
          setUserDatabases(result.data);
        }
      } catch (error) {
        console.error("Error fetching user databases after select:", error);
      } finally {
        setIsLoadingDatabases(false);
      }
    }
  };

  const handleDownload = async (getPresignedURL,fileName) => {
    try {
      console.log("The presigned URL is",getPresignedURL)
       const hostName = window.location.hostname
      const sanitizedHostName = hostName.replaceAll('.','-')
      const fullURL = `${
        "https://" +
        process.env.REACT_APP_MINIO_OBJECT_END_POINT +
        `/${sanitizedHostName}/` +
        getPresignedURL
      }`;
      const link = document.createElement("a");
      link.href = fullURL;
      link.download = fileName;
       link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fullURL);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleDbModalOpen = () => {
    // Reset connection state to initial values before opening modal
    updateConnectionDetails("account", "");
    updateConnectionDetails("username", "");
    updateConnectionDetails("password", "");
    updateConnectionDetails("warehouse", "");
    updateConnectionDetails("role", "");
    updateConnectionDetails("schema", "");
    updateConnectionDetails("database", "");
    if (connectionState.isConnected) {
      disconnect();
    }
    setOpenDbModal(true);
  };

  const handleDbModalClose = () => {
    setOpenDbModal(false);
  };

  const handleDbConnectionChange = (field) => (event) => {
    updateConnectionDetails(field, event.target.value);
  };

  const handleDbConnectionSubmit = async () => {
    try {
      await connect(connectionState.connectionDetails);
      // After successful connection, fetch all user databases
      if (userId) {
        console.log("connectionState", connectionState, userId);

        setIsLoadingDatabases(true);
        console.log("connectionState2", connectionState, userId);

        const result = await getAllUserDatabases(userId);
        setDbDetailsLoading(false);

        setIsLoadingDatabases(false);
        if (result.success && result.data && result.data.length > 0) {
          const firstDb = result.data[0];
          setSelectedDatabase(firstDb);
          localStorage.setItem(LOCAL_STORAGE_KEY, firstDb.user_db_id);
          window.dispatchEvent(new Event("selected_user_db_id_changed"));
          // Optionally, fetch connection details for the first DB (if needed)
          if (firstDb.user_db_id) {
            setDbDetailsLoading(true);
            try {
              const detailsResult = await getConnectionDetails(
                userId,
                firstDb.user_db_id
              );
              if (detailsResult.success) {
                let tablesData = detailsResult.data.tablesData;
                if (typeof tablesData === "string") {
                  try {
                    tablesData = JSON.parse(tablesData);
                  } catch (e) {
                    console.error("Error parsing tablesData:", e);
                  }
                }
                const datasourceId = detailsResult.data.user_db_id;
                localStorage.setItem(
                  "snowflake_connection_details",
                  JSON.stringify(detailsResult.data.connectionDetails || {})
                );
                localStorage.setItem(
                  "snowflake_tables_data",
                  JSON.stringify(tablesData)
                );
                if (datasourceId) {
                  localStorage.setItem("snowflake_datasource_id", datasourceId);
                }
                updateDatabaseState({
                  isConnected: true,
                  user_db_id: firstDb.user_db_id,
                  tablesData: tablesData,
                  datasourceId: datasourceId,
                });
              } else {
                localStorage.removeItem("snowflake_connection_details");
                localStorage.removeItem("snowflake_tables_data");
                localStorage.removeItem("snowflake_datasource_id");
                updateDatabaseState({
                  isConnected: false,
                  user_db_id: firstDb.user_db_id,
                  tablesData: null,
                  datasourceId: null,
                });
              }
            } catch (error) {
              console.error(
                "Error fetching connection details for selected DB:",
                error
              );
              localStorage.removeItem("snowflake_connection_details");
              localStorage.removeItem("snowflake_tables_data");
              localStorage.removeItem("snowflake_datasource_id");
              updateDatabaseState({
                isConnected: false,
                user_db_id: firstDb.user_db_id,
                tablesData: null,
                datasourceId: null,
              });
            } finally {
            }
          }
          // Redirect to /dashboard
          navigate("/dashboard");
        }
      }
      handleDbModalClose();
    } catch (error) {
      onError(error);
    }
  };

  const handleDisconnect = () => {
    disconnect();
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (typeof refetchNotifications === "function") {
      refetchNotifications().catch((error) => {
        console.error("Error refetching notifications:", error);
      });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markNotificationAsRead = async (notificationId) => {
    if (!readNotifications.has(notificationId)) {
      try {
        await markAsRead([notificationId]);
        setReadNotifications((prev) => new Set([...prev, notificationId]));
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
  };

  const handleNotificationAction = async (notification) => {
    console.log("[Topbar] Notification clicked:", {
      id: notification.notification_id,
      is_read: notification.is_read,
      action_link: notification.action_link,
      created_on: notification.created_on,
      // keep logs tidy
      message_preview:
        typeof notification.message === "string"
          ? notification.message.slice(0, 200)
          : notification.message,
    });
    await markNotificationAsRead(notification.notification_id);
    if (notification.action_link) {
      handleClose();
      if (notification.action_link.startsWith("downloads/")) {
        const fileName = notification.action_link.split("/").pop();
        handleDownload(notification.action_link, fileName);
      } else {
        const url = new URL(notification.action_link);
        const path = url.pathname;
        navigate(path);
      }
    }
  };

  const toggleMessageExpansion = async (notificationId, event) => {
    event.stopPropagation();
    console.log("[Topbar] ReadMore clicked →", notificationId);
    await markNotificationAsRead(notificationId);
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const isMessageLong = (message) => message.length > 100;

  const formatNotificationDate = (timestamp) => {
    try {
      if (!timestamp) return "Just now";

      let date;
      if (!isNaN(timestamp)) {
        const timestampNum = Number(timestamp);
        date = new Date(
          timestampNum.toString().length > 10
            ? timestampNum
            : timestampNum * 1000
        );
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) return "Just now";

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minutes ago`;
      if (diffHours < 24) return `${diffHours} hours ago`;
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;

      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Just now";
    }
  };

  const unreadCount = notification.filter(
    (notif) => !notif.is_read && !readNotifications.has(notif.notification_id)
  ).length;

  return (
    <StyledAppBar position="fixed">
      <StyledToolbar>
        <LogoContainer>
          {/* FD Logo Box (smaller, inside LogoContainer) */}
          <Box
            sx={{
              width: 32,
              height: 32,
              background: "#ffffff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                color: "rgb(44, 52, 123)",
                fontWeight: 600,
                fontSize: "1rem",
                letterSpacing: 1,
              }}
            >
              FD
            </span>
          </Box>
          <Tooltip
            title={
              selectedDatabase
                ? `Connected to ${
                    selectedDatabase.database || selectedDatabase.name
                }`
                : "Select Database"
            }
            placement="bottom"
            arrow
          >
            <ConnectionButton
              onClick={handleDbDropdownClick}
              data-connected={!!selectedDatabase}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                padding: "2px 8px",
                borderRadius: "4px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                backgroundColor: selectedDatabase
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(255,255,255,0.08)",
                border: "1px solid",
                borderColor: selectedDatabase
                  ? "rgba(255,255,255,0.25)"
                  : "rgba(255,255,255,0.12)",
                "&:hover": {
                  backgroundColor: selectedDatabase
                    ? "rgba(255,255,255,0.18)"
                    : "rgba(255,255,255,0.13)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <ConnectionIcon
                data-connected={!!selectedDatabase}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "20px",
                  height: "20px",
                  borderRadius: "4px",
                  backgroundColor: selectedDatabase
                    ? "rgba(255,255,255,0.18)"
                    : "rgba(255,255,255,0.10)",
                  color: "#fff",
                  position: "relative",
                  "& svg": {
                    fontSize: "14px",
                  },
                }}
              >
                {selectedDatabase ? (
                  <CloudDoneIcon sx={{ color: "#fff" }} />
                ) : (
                  <CloudQueueIcon sx={{ color: "#fff" }} />
                )}
                {selectedDatabase && <ConnectionPulse />}
              </ConnectionIcon>
              <ConnectionInfo>
                <ConnectionStatus
                  data-connected={!!selectedDatabase}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&::before": {
                      content: '""',
                      display: "block",
                      width: "4px",
                      height: "4px",
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      boxShadow: selectedDatabase
                        ? "0 0 0 2px rgba(255,255,255,0.18)"
                        : "none",
                    },
                  }}
                >
                  {selectedDatabase
                    ? selectedDatabase.database ||
                    selectedDatabase.database_name ||
                    selectedDatabase.name ||
                    "Connected"
                    : "Select DB"}
                </ConnectionStatus>
              </ConnectionInfo>
              <KeyboardArrowDownIcon
                sx={{ fontSize: 16, color: "#fff", ml: 0.5 }}
              />
            </ConnectionButton>
          </Tooltip>
        </LogoContainer>
        <ActionButtons>
          <StyledIconButton onClick={handleNotificationClick}>
            {unreadCount > 0 ? (
              <NotificationBadge
                badgeContent={unreadCount}
                max={99}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#FFD600",
                    color: "rgb(44, 52, 123)",
                  },
                }}
              >
                <NotificationsActiveIcon sx={{ color: "#fff" }} />
              </NotificationBadge>
            ) : (
              <NotificationsActiveIcon sx={{ color: "#fff" }} />
            )}
          </StyledIconButton>
        </ActionButtons>
      </StyledToolbar>

      {/* Database Dropdown Menu */}
      <StyledMenu
        anchorEl={dbDropdownAnchor}
        open={Boolean(dbDropdownAnchor)}
        onClose={handleDbDropdownClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 70,
            maxWidth: 100,
            borderRadius: 1,
            mt: 0.5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.06)",
            background: "#fff",
          },
        }}
      >
        <MenuHeader>
          <MenuTitle
            sx={{ fontSize: "0.8rem", fontWeight: 600, color: "text.primary" }}
          >
            Select Database
          </MenuTitle>
        </MenuHeader>
        {dbDetailsLoading ? (
          <MenuItem disabled sx={{ py: 1, px: 1, justifyContent: "center" }}>
            <CircularProgress size={20} />
            <Typography
              variant="caption"
              sx={{ fontSize: "0.7rem", color: "text.disabled", ml: 1 }}
            >
              Loading database details...
            </Typography>
          </MenuItem>
        ) : isLoadingDatabases ? (
          <MenuItem disabled sx={{ py: 0.5, px: 1 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.65rem", color: "text.disabled" }}
            >
              Loading databases...
            </Typography>
          </MenuItem>
        ) : userDatabases.length === 0 ? (
          [
            <MenuItem key="no-db" sx={{ py: 0.5, px: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontSize: "0.65rem", color: "text.disabled" }}
              >
                No databases found
              </Typography>
            </MenuItem>,
            <MenuItem
              key="add-new-db"
              onClick={() => handleDatabaseSelect("add_new")}
              sx={{
                py: 0.5,
                px: 1,
                minHeight: "auto",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  width: "100%",
                }}
              >
                <AddIcon
                  sx={{ fontSize: 12, color: "#1976d2", flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 500,
                    color: "#1976d2",
                  }}
                >
                  Add New DB
                </Typography>
              </Box>
            </MenuItem>,
          ]
        ) : (
          [
            ...userDatabases.map((db) => (
              <MenuItem
                key={db.user_db_id}
                onClick={() => handleDatabaseSelect(db)}
                selected={selectedDatabase?.user_db_id === db.user_db_id}
                sx={{
                  py: 0.5,
                  px: 1,
                  minHeight: "auto",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.12)",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "100%",
                  }}
                >
                  <CloudDoneIcon
                    sx={{
                      fontSize: 12,
                      color:
                        selectedDatabase?.user_db_id === db.user_db_id
                          ? "#1976d2"
                          : "#757575",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight:
                        selectedDatabase?.user_db_id === db.user_db_id
                          ? 500
                          : 400,
                      color:
                        selectedDatabase?.user_db_id === db.user_db_id
                          ? "#1976d2"
                          : "#424242",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {db.database ||
                      db.database_name ||
                      db.name ||
                      `DB ${db.user_db_id}`}
                  </Typography>
                </Box>
              </MenuItem>
            )),
            <Divider key="divider" sx={{ my: 0.25, mx: 0.75 }} />,
            <MenuItem
              key="add-new-db"
              onClick={() => handleDatabaseSelect("add_new")}
              sx={{
                py: 0.5,
                px: 1,
                minHeight: "auto",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  width: "100%",
                }}
              >
                <AddIcon
                  sx={{ fontSize: 12, color: "#1976d2", flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 500,
                    color: "#1976d2",
                  }}
                >
                  Add New DB
                </Typography>
              </Box>
            </MenuItem>,
          ]
        )}
      </StyledMenu>

      {/* Database Connection Modal */}
      <Dialog
        open={openDbModal}
        onClose={() => {}}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            background: "linear-gradient(to bottom, #ffffff, #fafafa)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  backgroundColor: connectionState.isConnected
                    ? "rgba(76, 175, 80, 0.1)"
                    : "rgba(25, 118, 210, 0.1)",
                  color: connectionState.isConnected ? "#4CAF50" : "#1976d2",
                }}
              >
                {connectionState.isConnected ? (
                  <CloudDoneIcon sx={{ fontSize: 20 }} />
                ) : (
                  <CloudQueueIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
              <Typography
                variant="subtitle1"
                component="div"
                sx={{ fontWeight: 600 }}
              >
                Database Connection
              </Typography>
            </Box>
            {connectionState.isConnected && (
              <Chip
                label="Connected"
                color="success"
                size="small"
                icon={<CloudDoneIcon sx={{ fontSize: 14 }} />}
                sx={{
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  color: "#4CAF50",
                  fontWeight: 500,
                  height: "24px",
                  "& .MuiChip-label": {
                    px: 1,
                    fontSize: "11px",
                  },
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 2.5, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <InputField
              label="Account"
              value={connectionState.connectionDetails.account}
              onChange={handleDbConnectionChange("account")}
              placeholder="Enter account name"
              fullWidth
              size="small"
            />
            <InputField
              label="Username"
              value={connectionState.connectionDetails.username}
              onChange={handleDbConnectionChange("username")}
              placeholder="Enter username"
              fullWidth
              size="small"
            />
            <InputField
              label="Password"
              type="password"
              value={connectionState.connectionDetails.password}
              onChange={handleDbConnectionChange("password")}
              placeholder="Enter password"
              fullWidth
              size="small"
            />
            <InputField
              label="Warehouse"
              value={connectionState.connectionDetails.warehouse}
              onChange={handleDbConnectionChange("warehouse")}
              placeholder="Enter warehouse name"
              fullWidth
              size="small"
            />
            <InputField
              label="Role"
              value={connectionState.connectionDetails.role}
              onChange={handleDbConnectionChange("role")}
              placeholder="Enter role"
              fullWidth
              size="small"
            />
            <InputField
              label="Schema"
              value={connectionState.connectionDetails.schema}
              onChange={handleDbConnectionChange("schema")}
              placeholder="Enter schema name"
              fullWidth
              size="small"
            />
            <InputField
              label="Database"
              value={connectionState.connectionDetails.database}
              onChange={handleDbConnectionChange("database")}
              placeholder="Enter database name"
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          {connectionState.isConnected ? (
            <Button
              onClick={handleDisconnect}
              color="error"
              variant="outlined"
              startIcon={<CloudOffIcon sx={{ fontSize: 16 }} />}
              size="small"
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Disconnect
            </Button>
          ) : (
            <>
              <Button
                onClick={handleDbModalClose}
                color="inherit"
                size="small"
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDbConnectionSubmit}
                variant="contained"
                color="primary"
                startIcon={<CloudDoneIcon sx={{ fontSize: 16 }} />}
                disabled={
                  !connectionState.connectionDetails.account ||
                  !connectionState.connectionDetails.username ||
                  !connectionState.connectionDetails.password ||
                  connectionState.loading
                }
                size="small"
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 500,
                  boxShadow: "0 2px 8px rgba(25, 118, 210, 0.25)",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.35)",
                  },
                }}
              >
                {connectionState.loading ? "Connecting..." : "Connect"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuHeader>
          <MenuTitle>
            Notifications {unreadCount > 0 && `(${unreadCount} new)`}
          </MenuTitle>
        </MenuHeader>

        <NotificationList>
          {notification.length === 0 ? (
            <MenuItem>
              <Typography variant="body2">No notifications</Typography>
            </MenuItem>
          ) : (
            notification.map((notif) => {
              const isUnread =
                !notif.is_read && !readNotifications.has(notif.notification_id);
              return (
                <NotificationListItem
                  key={notif.notification_id}
                  isUnread={isUnread}
                  onClick={() =>
                    console.log(
                      "[Topbar] NotificationListItem onClick (bubble) →",
                      notif.notification_id
                    )
                  }
                >
                  <Box
                    sx={{ width: "100%", cursor: "pointer" }}
                    onClick={() => {
                      console.log(
                        "[Topbar] Row Box onClick →",
                        notif.notification_id
                      );
                      handleNotificationAction(notif);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleNotificationAction(notif);
                        console.log(
                          "[Topbar] Row Box onKeyDown →",
                          e.key,
                          notif.notification_id
                        );
                      }
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      {isUnread && (
                        <FiberManualRecordIcon
                          sx={{
                            fontSize: 10,
                            color: "error.main",
                            mt: 0.5,
                            animation: "pulse 2s infinite",
                            "@keyframes pulse": {
                              "0%": { opacity: 1 },
                              "50%": { opacity: 0.5 },
                              "100%": { opacity: 1 },
                            },
                          }}
                        />
                      )}
                      <NotificationMessage
                        sx={{
                          maxHeight: expandedMessages.has(notif.notification_id)
                            ? "none"
                            : "2.6em",
                          WebkitLineClamp: expandedMessages.has(
                            notif.notification_id
                          )
                            ? "unset"
                            : 2,
                          fontWeight: isUnread ? 600 : 400,
                          color: isUnread ? "text.primary" : "text.secondary",
                        }}
                      >
                        {notif.message}
                      </NotificationMessage>
                    </Box>

                    {isMessageLong(notif.message) && (
                      <ReadMoreButton
                        onClick={(e) =>
                          toggleMessageExpansion(notif.notification_id, e)
                        }
                      >
                        {expandedMessages.has(notif.notification_id)
                          ? "Read less"
                          : "Read more"}
                      </ReadMoreButton>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 0.5,
                      }}
                    >
                      <NotificationTime>
                        <AccessTimeIcon />
                        {formatNotificationDate(notif.created_on)}
                      </NotificationTime>
                      {(() => {
                        // Don't show action button for CSV_JOB_ALERT with null/empty action_link
                        if (notif.notification_type === "CSV_JOB_ALERT" && (!notif.action_link || notif.action_link.trim() === "")) {
                          return null;
                        }

                        // Show action button for other cases
                        if (notif.action_link) {
                          return (
                            <NotificationLink
                              onClick={async (e) => {
                                e.preventDefault(); // Prevent default link behavior
                                e.stopPropagation(); // Stop event bubbling

                                console.log(
                                  "[Topbar] Action link clicked →",
                                  notif.notification_id,
                                  notif
                                );

                                // Handle CSV job download
                                if (notif.notification_type === "CSV_JOB_ALERT" && notif.action_link) {
                                  try {
                                    const result = await downloadFileUrl(notif.action_link);
                                    console.log("[Topbar] DownloadFileUrl result →", result);

                                    // Prefer presigned URL from backend; fall back to original action_link
                                    const downloadUrl =
                                      result?.success && result.data?.presigned_url
                                        ? result.data.presigned_url
                                        : "";
                                        
                                    console.log("The presigned_URL In frontend is", downloadUrl)
                                      const fileName = notif.action_link.split('/').pop()|| 'download.csv'
                                      console.log("The final name in download function is",fileName)
                                    // Method 1: Try fetch + blob download (more reliable)
                                    handleDownload (downloadUrl,fileName)
                                  } catch (error) {
                                    console.error('❌ Download failed:', error);
                                    // Final fallback: open in new tab
                                    window.open(notif.action_link, '_blank');
                                  }
                                } else if (notif.action_link) {
                                  // Handle other notification types (navigate to action_link)
                                  try {
                                    const url = new URL(notif.action_link);
                                    const path = url.pathname;
                                    navigate(path);
                                  } catch (error) {
                                    console.error('❌ Navigation failed:', error);
                                    // Fallback: direct navigation
                                    window.location.href = notif.action_link;
                                  }
                                }
                              }}
                            >
                              {notif.notification_type === "CSV_JOB_ALERT" ? (
                                <>
                                  <DownloadIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                  Download
                                </>
                              ) : (
                                <>
                                  View
                                  <ChevronRightIcon />
                                </>
                              )}
                            </NotificationLink>
                          );
                        }

                        return null;
                      })()}
                    </Box>
                  </Box>
                </NotificationListItem>
              );
            })
          )}
        </NotificationList>
      </StyledMenu>
    </StyledAppBar>
  );
};

export default Topbar;
